import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveSeo, SITE_URL } from '@/lib/seo';
import { injectJsonLd, removeJsonLd } from '@/lib/jsonld';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  document.head
    .querySelectorAll(`meta[${attr}="${key}"]`)
    .forEach((el) => el.remove());
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeCanonical() {
  document.head
    .querySelectorAll('link[rel="canonical"]')
    .forEach((el) => el.remove());
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const seo = resolveSeo(location.pathname);
    document.title = seo.title;
    upsertMeta(
      'name',
      'robots',
      seo.noindex ? 'noindex, nofollow' : 'index, follow',
    );

    if (seo.noindex) {
      // 비인덱스 페이지: 이전 라우트의 메타데이터가 새어나가지 않도록 정리
      removeMeta('name', 'description');
      removeMeta('name', 'keywords');
      removeMeta('property', 'og:title');
      removeMeta('property', 'og:description');
      removeMeta('property', 'og:url');
      removeMeta('name', 'twitter:title');
      removeMeta('name', 'twitter:description');
      removeCanonical();
      removeJsonLd();
      return;
    }

    const canonical = `${SITE_URL}${seo.path === '/' ? '/' : seo.path}`;

    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', seo.keywords);
    upsertCanonical(canonical);
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);

    removeJsonLd();
    injectJsonLd(seo.path);
  }, [location.pathname]);

  return null;
}